import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { fabric } from "fabric";
import "./spotMapPage.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";

const CLOSING_THRESHOLD = 10;

const PLACEHOLDER_SVG = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="600" height="400"><rect fill="#e8e8ec" width="100%" height="100%"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#999" font-family="system-ui,sans-serif" font-size="15">No image</text></svg>'
)}`;

function parseMapping(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

function errMessage(e) {
  if (e == null) return "Unknown error";
  if (typeof e === "string") return e;
  return e.message || String(e);
}

function deriveLeftImageKey(rightKey) {
  if (rightKey === "spotRightImage") return "spotLeftImage";
  const m = /^spotRightImage(\d+)$/.exec(rightKey);
  return m ? `spotLeftImage${m[1]}` : null;
}

const SpotMapPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((s) => s.theme);

  const svgKey = searchParams.get("svgKey") || "spotSvgMapping";
  const imageKey = searchParams.get("imageKey") || "spotRightImage";
  const showNormalPanel =
    searchParams.get("doubleImage") === "1" ||
    Boolean(searchParams.get("leftImageKey"));

  const leftImageKeyParam = searchParams.get("leftImageKey");
  const leftImageKey = useMemo(() => {
    if (leftImageKeyParam) return leftImageKeyParam;
    return deriveLeftImageKey(imageKey);
  }, [leftImageKeyParam, imageKey]);

  const canvasElRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const canvasRef = useRef(null);
  const polygonAnswersRef = useRef({});
  const currentObjectRef = useRef(null);
  const drawRef = useRef({
    isDrawing: false,
    polygonPoints: [],
    tempLine: null,
    tempLines: [],
  });

  const [spotName, setSpotName] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [showMapButton, setShowMapButton] = useState(true);
  const [imageReady, setImageReady] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  const s3Base = process.env.REACT_APP_S3_PATH || "";

  const imageFileName = data?.[imageKey];
  const imageFullUrl =
    imageFileName && typeof imageFileName === "string"
      ? `${s3Base}${imageFileName}`
      : null;

  const leftFileName =
    showNormalPanel && leftImageKey ? data?.[leftImageKey] : null;
  const leftFullUrl =
    leftFileName && typeof leftFileName === "string"
      ? `${s3Base}${leftFileName}`
      : showNormalPanel
        ? PLACEHOLDER_SVG
        : null;

  const mappingFingerprint = JSON.stringify(parseMapping(data?.[svgKey]));

  const resetDrawingState = useCallback((canvas) => {
    const d = drawRef.current;
    d.tempLines.forEach((line) => canvas.remove(line));
    d.tempLines = [];
    d.tempLine = null;
    d.polygonPoints = [];
    d.isDrawing = false;
    canvas.selection = true;
    setShowMapButton(true);
  }, []);

  const completePolygon = useCallback(
    (canvas) => {
      const d = drawRef.current;
      if (d.polygonPoints.length > 2) {
        d.tempLines.forEach((line) => canvas.remove(line));
        d.tempLines = [];
        d.tempLine = null;

        const polygonId = `polygon_${canvas.getObjects("polygon").length + 1}`;
        const polygon = new fabric.Polygon(d.polygonPoints, {
          fill: "rgba(0, 0, 255, 0.3)",
          stroke: "blue",
          selectable: true,
        });
        polygon.set("id", polygonId);
        canvas.add(polygon);
        polygon.set({
          hasControls: false,
          lockMovementX: true,
          lockMovementY: true,
          lockScalingX: true,
          lockScalingY: true,
          lockRotation: true,
        });
        canvas.renderAll();
        polygonAnswersRef.current[polygonId] = "New Object";
        setShowDetails(true);
        currentObjectRef.current = polygonId;
        setSpotName("New Object");
      }
      resetDrawingState(canvas);
    },
    [resetDrawingState]
  );

  const reinitializePolygons = useCallback((canvas, polygonData) => {
    polygonAnswersRef.current = {};
    polygonData.forEach((item) => {
      const polygon = new fabric.Polygon(item.points, {
        fill: "rgba(0, 0, 255, 0.3)",
        stroke: "blue",
        selectable: true,
      });
      polygon.set("id", item.id);
      canvas.add(polygon);
      polygon.set({
        hasControls: false,
        lockMovementX: true,
        lockMovementY: true,
        lockScalingX: true,
        lockScalingY: true,
        lockRotation: true,
      });
      polygonAnswersRef.current[item.id] = item.answer || "";
    });
    canvas.renderAll();
  }, []);

  const exportPolygons = useCallback((canvas) => {
    const polygons = canvas.getObjects("polygon");
    return polygons.map((polygon) => ({
      id: polygon.id,
      points: polygon.points.map((p) => ({ x: p.x, y: p.y })),
      answer: polygonAnswersRef.current[polygon.id] || "",
    }));
  }, []);

  /** Wider on-screen canvas; internal coords stay 600×400 (cssOnly). */
  const applyCanvasDisplaySize = useCallback((canvas) => {
    const wrap = canvasWrapRef.current;
    if (!wrap || !canvas) return;
    const cw = wrap.clientWidth;
    if (cw < 40) return;
    const cssW = Math.min(1200, Math.max(300, cw));
    const cssH = Math.round(cssW * (400 / 600));
    canvas.setDimensions({ width: cssW, height: cssH }, { cssOnly: true });
  }, []);

  useEffect(() => {
    if (!imageFullUrl || !canvasElRef.current) {
      if (canvasRef.current) {
        canvasRef.current.dispose();
        canvasRef.current = null;
      }
      setImageReady(false);
      return undefined;
    }

    setImageReady(false);
    const initial = parseMapping(data?.[svgKey]);

    if (canvasRef.current) {
      canvasRef.current.dispose();
      canvasRef.current = null;
    }

    const canvas = new fabric.Canvas(canvasElRef.current, {
      width: 600,
      height: 400,
      backgroundColor: "#f3f3f3",
      selection: true,
    });
    canvasRef.current = canvas;
    polygonAnswersRef.current = {};

    const onMouseDown = (options) => {
      const d = drawRef.current;
      if (d.isDrawing) {
        const pointer = canvas.getPointer(options.e);
        const point = { x: pointer.x, y: pointer.y };

        if (d.polygonPoints.length === 0) {
          d.polygonPoints.push(point);
          d.tempLine = new fabric.Line([point.x, point.y, point.x, point.y], {
            stroke: "red",
            selectable: false,
          });
          canvas.add(d.tempLine);
          d.tempLines.push(d.tempLine);
        } else {
          const firstPoint = d.polygonPoints[0];
          const distance = Math.sqrt(
            (firstPoint.x - point.x) ** 2 + (firstPoint.y - point.y) ** 2
          );
          if (distance < CLOSING_THRESHOLD) {
            completePolygon(canvas);
          } else {
            d.polygonPoints.push(point);
            d.tempLine.set({ x2: point.x, y2: point.y });
            canvas.renderAll();
            d.tempLine = new fabric.Line([point.x, point.y, point.x, point.y], {
              stroke: "red",
              selectable: false,
            });
            canvas.add(d.tempLine);
            d.tempLines.push(d.tempLine);
          }
        }
        return;
      }

      if (options.target && options.target.type === "polygon") {
        const polygon = options.target;
        const polygonId = polygon.id;
        setShowDetails(true);
        currentObjectRef.current = polygonId;
        setSpotName(polygonAnswersRef.current[polygonId] || "");
      }
    };

    const onMouseMove = (options) => {
      const d = drawRef.current;
      if (d.isDrawing && d.tempLine) {
        const pointer = canvas.getPointer(options.e);
        d.tempLine.set({ x2: pointer.x, y2: pointer.y });
        canvas.renderAll();
      }
    };

    canvas.on("mouse:down", onMouseDown);
    canvas.on("mouse:move", onMouseMove);

    fabric.Image.fromURL(
      imageFullUrl,
      (img) => {
        if (!canvasRef.current || canvasRef.current !== canvas) return;
        canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
          scaleX: canvas.width / img.width,
          scaleY: canvas.height / img.height,
        });
        if (initial.length) {
          reinitializePolygons(canvas, initial);
        }
        applyCanvasDisplaySize(canvas);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (canvasRef.current === canvas) applyCanvasDisplaySize(canvas);
          });
        });
        setImageReady(true);
      },
      { crossOrigin: "anonymous" }
    );

    const wrap = canvasWrapRef.current;
    const ro =
      typeof ResizeObserver !== "undefined" && wrap
        ? new ResizeObserver(() => {
            if (canvasRef.current) applyCanvasDisplaySize(canvasRef.current);
          })
        : null;
    if (ro && wrap) ro.observe(wrap);

    return () => {
      if (ro && wrap) ro.disconnect();
      canvas.off("mouse:down", onMouseDown);
      canvas.off("mouse:move", onMouseMove);
      canvas.dispose();
      if (canvasRef.current === canvas) canvasRef.current = null;
    };
  }, [
    imageFullUrl,
    mappingFingerprint,
    svgKey,
    data,
    completePolygon,
    reinitializePolygons,
    applyCanvasDisplaySize,
  ]);

  const enableDrawingMode = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawRef.current.isDrawing = true;
    canvas.selection = false;
    setShowMapButton(false);
    Swal.fire({
      icon: "info",
      title: "Draw a polygon",
      text: "Click around the object you want to highlight, then click near the first point to close the shape.",
    });
  };

  const cancelDrawingIfAny = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (drawRef.current.isDrawing) {
      resetDrawingState(canvas);
    }
  };

  const handleSaveObjectName = () => {
    const id = currentObjectRef.current;
    if (id) polygonAnswersRef.current[id] = spotName;
    setShowDetails(false);
  };

  const handleDeleteObject = () => {
    const canvas = canvasRef.current;
    const id = currentObjectRef.current;
    if (!canvas || !id) return;
    const obj = canvas.getObjects().find((o) => o.id === id);
    if (obj) canvas.remove(obj);
    delete polygonAnswersRef.current[id];
    setShowDetails(false);
    canvas.renderAll();
  };

  const handleExport = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const exportData = exportPolygons(canvas);
    const themeId = currentTheme || data?.themename;
    if (!themeId) {
      Swal.fire("Error", "No theme selected.", "error");
      return;
    }
    try {
      await updateThemeData({
        payload: {
          data: { [svgKey]: exportData },
          currentTheme: themeId,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId })).unwrap();
      Swal.fire("Success", "Objects updated.", "success");
    } catch (e) {
      Swal.fire("Error", errMessage(e) || "Update failed", "error");
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button
          type="button"
          className="back-button"
          onClick={() => navigate("/admin/customize-spot")}
        >
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="spot-map-page">
        {showNormalPanel && (
          <p className="spot-map-hint">
            <strong>Normal</strong> and <strong>changed</strong> images are side by side. Draw
            polygons only on the right image where the differences are.
          </p>
        )}
        {!imageFullUrl && (
          <p style={{ color: "#666", marginBottom: 16 }}>
            No image for this layer. Upload an &quot;Image with change&quot; on the Spot images
            page first.
          </p>
        )}

        <div
          className={`spot-map-row ${showNormalPanel ? "" : "spot-map-row--single"}`}
        >
          {showNormalPanel && (
            <section className="spot-map-col spot-map-col--reference" aria-label="Reference">
              <h2 className="spot-map-section-title">Normal image (reference)</h2>
              <div className="spot-map-visual-shell">
                <img
                  className="spot-map-ref-img"
                  src={leftFullUrl || PLACEHOLDER_SVG}
                  alt=""
                  onError={(e) => {
                    e.target.src = PLACEHOLDER_SVG;
                  }}
                />
              </div>
            </section>
          )}

          <section
            className={`spot-map-col spot-map-col--draw ${showNormalPanel ? "" : "spot-map-col--draw-only"}`}
            aria-label="Map differences"
          >
            <h2 className="spot-map-section-title">Image with change — draw map here</h2>
            <div className="spot-map-visual-shell spot-map-visual-shell--canvas">
              <div ref={canvasWrapRef} className="spot-map-canvas-inner">
                <canvas ref={canvasElRef} />
              </div>
            </div>
            {imageFullUrl && !imageReady && (
              <p className="spot-map-loading">Loading image…</p>
            )}
          </section>
        </div>

        <section className="spot-map-details-panel" aria-label="Object details">
          <h3 className="spot-map-details-heading">Object details</h3>
          <hr className="spot-map-details-hr" />
          {showDetails ? (
            <div className="spot-map-details-body">
              <h4 className="spot-map-details-sub">Object name:</h4>
              <input
                type="text"
                className="spot-map-details-input"
                value={spotName}
                onChange={(e) => setSpotName(e.target.value)}
                placeholder="Spot name"
              />
              <span className="spot-map-note">
                Shown when the user taps this difference.
              </span>
              <div className="spot-map-details-btns">
                <button
                  type="button"
                  className="spot-map-btn-info"
                  onClick={handleSaveObjectName}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="spot-map-btn-danger"
                  onClick={handleDeleteObject}
                >
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <p className="spot-map-details-placeholder">
              Draw a new object or click an existing blue shape to name or delete it.
            </p>
          )}
        </section>

        <div className="spot-map-actions">
          {showMapButton && (
            <button
              type="button"
              className="spot-map-btn-map"
              onClick={enableDrawingMode}
            >
              Map new object
            </button>
          )}
          {!showMapButton && (
            <button
              type="button"
              className="spot-map-btn-map"
              onClick={cancelDrawingIfAny}
            >
              Cancel drawing
            </button>
          )}
          <button
            type="button"
            className="spot-map-btn-update"
            onClick={handleExport}
          >
            Update
          </button>
        </div>
      </div>
    </>
  );
};

export default SpotMapPage;
